// Hand-written Q&A blocks for the articles that already appear in Google
// Search Console for question-shaped queries but rank on page 4-9.
//
// Every question below is a real query string Search Console recorded for
// that exact URL in the last 30 days. Answers are written by hand from
// established, checkable science - no AI generation, no invented numbers.
// They render as visible content plus FAQPage structured data, which is what
// Google needs to match the page to the query.

export type Faq = { q: string; a: string };

export const ARTICLE_FAQS: Record<string, Faq[]> = {
  "animal-that-can-survive-space": [
    {
      q: "Can tardigrades survive in space?",
      a: "Yes - briefly. In the 2007 FOTON-M3 mission, dehydrated tardigrades were exposed to the vacuum and radiation of low Earth orbit for 10 days, and some survived and went on to reproduce. They do not live in space; they shut down into a dried-out state called a tun and wait it out.",
    },
    {
      q: "How long can tardigrades survive in space?",
      a: "The documented exposure is on the order of days to weeks in low Earth orbit. Survival drops sharply with unfiltered solar UV, which is the part of space that kills them fastest - not the vacuum.",
    },
    {
      q: "Can tardigrades survive on Mars, Venus or Jupiter?",
      a: "No. A dried tardigrade could plausibly persist on the Martian surface for a while, but it cannot grow or reproduce without liquid water. Venus's surface is around 465 °C and Jupiter has no surface at all - neither is survivable in any state.",
    },
    {
      q: "Are there tardigrades on the Moon?",
      a: "Probably, in dried form. The Israeli Beresheet lander crashed on the Moon in 2019 carrying dehydrated tardigrades. Nobody has recovered them, and reviving them would require liquid water, which the impact site does not provide.",
    },
    {
      q: "Did tardigrades come from space?",
      a: "No. Tardigrades are an ordinary Earth animal group with a fossil record going back to the Cambrian. Their extreme tolerance evolved for surviving ponds and moss that dry out, not for vacuum.",
    },
  ],

  "sharks-dont-fear-humans-because-they-cant-see-us": [
    {
      q: "Are sharks blind?",
      a: "No. Shark eyes are built for dim water: a reflective layer behind the retina, the tapetum lucidum, boosts low-light sensitivity. Detail vision is poorer than ours, but they see shapes, contrast and movement well.",
    },
    {
      q: "Can sharks sense fear?",
      a: "There is no evidence sharks detect fear. What they do detect is electricity - the ampullae of Lorenzini pick up the tiny electric fields every muscle makes - plus pressure waves from erratic movement. Panicked splashing is detectable; the emotion behind it is not.",
    },
    {
      q: "Do sharks remember faces or people?",
      a: "Some species learn and remember individual humans in captivity and at regular feeding sites, mostly by association rather than facial recognition. Evidence for face recognition specifically is thin.",
    },
    {
      q: "Can sharks see in front of them?",
      a: "Most sharks have eyes on the sides of the head, giving very wide coverage with a blind spot directly in front of the snout and directly behind. Close in, they switch to electroreception and smell.",
    },
  ],

  "leafcutter-ants-fungus-farmers": [
    {
      q: "Do leafcutter ants eat the leaves they carry?",
      a: "No. The leaves are compost. The ants chew them into a mulch that feeds a fungus, and they eat the fungus.",
    },
    {
      q: "What fungus do leafcutter ants grow?",
      a: "Species in the genus Leucoagaricus, cultivated in underground gardens. The fungus grows nutrient-rich swollen tips called gongylidia, which the colony harvests as food.",
    },
    {
      q: "How long have ants been farming fungus?",
      a: "Fungus farming in ants goes back roughly 50-60 million years, tens of millions of years before human agriculture. Leafcutters are the most specialised branch of that lineage.",
    },
  ],

  "cinnamon-bark-spice-tree": [
    {
      q: "Does cinnamon come from a tree?",
      a: "Yes - from the inner bark of evergreen trees in the genus Cinnamomum. The bark is stripped, and as it dries it curls into the quills sold as cinnamon sticks.",
    },
    {
      q: "Is cinnamon a herb or a spice?",
      a: "A spice. Herbs come from leaves; spices come from bark, seeds, roots, buds or fruit. Cinnamon is bark.",
    },
    {
      q: "Are bay leaf and cinnamon from the same tree?",
      a: "No. Both belong to the laurel family, Lauraceae, but bay leaves come from Laurus nobilis and cinnamon from Cinnamomum species. Related family, different trees, different parts.",
    },
    {
      q: "Do cinnamon trees smell like cinnamon?",
      a: "The bark and freshly cut wood do, strongly. The intact standing tree is far more subtle - the aroma compound, cinnamaldehyde, is concentrated in the inner bark.",
    },
  ],

  "ghost-fishing-nets-eternal-traps": [
    {
      q: "What are ghost nets?",
      a: "Fishing nets, lines and traps that were lost or abandoned at sea and keep catching animals with nobody to collect them. The gear fishes indefinitely, which is where the name comes from.",
    },
    {
      q: "How long do ghost nets last in the ocean?",
      a: "Modern gear is nylon and other synthetic polymers, which do not biodegrade in seawater. They fragment slowly over decades to centuries, staying dangerous the entire time.",
    },
    {
      q: "Why are ghost nets so hard to remove?",
      a: "They drift, sink, snag on reefs and wrecks, and are usually invisible from the surface. Recovery is mostly manual diver work, one net at a time.",
    },
  ],

  "freeze-branding-the-scar-science": [
    {
      q: "How does freeze branding work?",
      a: "A branding iron chilled in liquid nitrogen or a dry-ice-and-alcohol bath is held against the skin. The cold destroys the pigment-producing melanocytes in the hair follicles without killing the follicle itself.",
    },
    {
      q: "Does freeze branding last?",
      a: "Yes. The hair regrows permanently white in the shape of the iron, so the mark stays readable for the animal's life.",
    },
    {
      q: "Is cold branding better than hot branding?",
      a: "It generally causes less tissue damage and produces a cleaner, more legible mark, but it takes longer to apply and requires a cryogenic supply on site.",
    },
  ],

  "butterfly-feet-tasting-sensors": [
    {
      q: "Do butterflies taste with their feet?",
      a: "Yes. Their tarsi - the foot segments - carry chemoreceptor hairs that respond to sugars and plant chemicals on contact.",
    },
    {
      q: "Why do butterflies taste with their feet?",
      a: "It is a landing test. A female drums a leaf with her legs to confirm it is the right host plant before laying eggs, because her caterpillars cannot move to a better one.",
    },
  ],

  "roadrunner-built-for-speed": [
    {
      q: "How fast can a roadrunner run?",
      a: "The greater roadrunner tops out around 32 km/h (about 20 mph) on the ground - fast for a bird that mostly walks, and far slower than the cartoon suggests.",
    },
    {
      q: "Can roadrunners fly?",
      a: "Yes, but badly and briefly - short glides to cross gaps or reach a perch. Running is the primary mode of travel and hunting.",
    },
  ],

  "neutron-star-weight-teaspoon": [
    {
      q: "How much does a teaspoon of a neutron star weigh?",
      a: "Roughly a billion tonnes. Neutron star material sits at about 10^17 kg per cubic metre, comparable to the density inside an atomic nucleus.",
    },
    {
      q: "Why is neutron star matter so dense?",
      a: "Gravity crushes protons and electrons together into neutrons, removing the empty space that makes ordinary atoms mostly nothing. What remains is packed nuclear matter.",
    },
  ],

  "mantis-shrimp-boiling-punch": [
    {
      q: "Does a mantis shrimp punch really boil water?",
      a: "Effectively, yes. The strike is so fast that pressure drops behind the club and a cavitation bubble forms and collapses, producing a brief flash of extreme heat and light in a microscopic volume of water.",
    },
    {
      q: "How hot does a mantis shrimp strike get?",
      a: "The collapsing cavitation bubble reaches thousands of degrees for a few billionths of a second. The surrounding water stays cold - there is not enough energy or time to heat anything you could measure with a thermometer.",
    },
    {
      q: "Why does the strike hit twice?",
      a: "The club lands the first blow, and the collapsing bubble delivers a second shockwave a fraction of a millisecond later. That is why prey can be stunned even when the club narrowly misses.",
    },
  ],

  "deep-dive-dubai-artificial-underwater-city": [
    {
      q: "How deep is Deep Dive Dubai?",
      a: "60 metres. It opened in 2021 in the Nad Al Sheba district and holds about 14 million litres of fresh water, making it the deepest pool built for diving.",
    },
    {
      q: "What is the sunken city in the Dubai pool?",
      a: "A purpose-built film set on the pool floor: apartments, a garage, an arcade and a library, all flooded on purpose so divers can swim through streets and rooms rather than an empty tank.",
    },
    {
      q: "Is the underwater city in Dubai real?",
      a: "It is real, but it is not a drowned town. Nothing sank - the whole thing was constructed inside a pool and then filled with water.",
    },
  ],

  "australias-animals-are-one-of-a-kind": [
    {
      q: "What animals are found only in Australia?",
      a: "Kangaroos, koalas, wombats, quokkas, Tasmanian devils and the platypus are Australian and nowhere else in the wild. Echidnas are shared with New Guinea, so they are not strictly Australia-only.",
    },
    {
      q: "Why are so many Australian animals unique?",
      a: "Isolation. Australia separated from the rest of Gondwana tens of millions of years ago and its animals evolved without the placental mammals that took over other continents, leaving marsupials to fill those roles.",
    },
    {
      q: "Are most Australian mammals endemic?",
      a: "Yes. The large majority of Australia's native land mammals live naturally nowhere else, which is an unusually high share compared with other continents.",
    },
  ],

  "airplane-sewage-waste-dont-fall-for-it": [
    {
      q: "Do planes dump waste in the air?",
      a: "No. Aircraft toilets use a vacuum system that pulls waste into sealed tanks. The tanks can only be emptied on the ground by a service truck - there is no in-flight release valve for the crew.",
    },
    {
      q: "What is blue ice falling from the sky?",
      a: "Rare leakage. If a service panel seal fails, a small amount of disinfectant-treated waste can seep out, freeze at altitude and later fall as a lump of blue ice. It is a fault, not normal operation.",
    },
    {
      q: "How do airplanes dispose of toilet waste?",
      a: "At the gate. A lavatory truck connects to the aircraft, drains the holding tanks, and the waste goes into the airport's normal sewage system.",
    },
  ],

  "el-nino-global-warming-worst-weather": [
    {
      q: "Does El Niño cause warmer weather?",
      a: "It raises global average surface temperature temporarily. El Niño is the warm phase of the Pacific cycle: heat stored in the ocean is released to the atmosphere, so El Niño years usually run hotter than the years either side of them.",
    },
    {
      q: "Is El Niño the same as global warming?",
      a: "No. El Niño is a natural cycle that swings back and forth every few years. Global warming is a long-term upward trend. El Niño rides on top of that trend, which is why recent El Niño years break records that older ones did not.",
    },
    {
      q: "Does El Niño cause heatwaves?",
      a: "It shifts the odds. El Niño changes where heat and rain land, making heatwaves and drought more likely in some regions and heavy rain more likely in others, rather than causing any single event on its own.",
    },
  ],

  "humpback-whales-bubble-net-feeding": [
    {
      q: "What is a bubble net?",
      a: "A spiral curtain of bubbles. One or more humpbacks swim upward in a circle while releasing air, and the rising bubbles act like a wall that small fish will not cross, packing the shoal into a tight ball.",
    },
    {
      q: "How do humpback whales hunt with bubble nets?",
      a: "As a team. Some whales blow the net, others herd and call, and then the group lunges up through the middle with mouths open. Not all humpback populations do it - it is learned behaviour passed between individuals.",
    },
  ],

  "jaguar-crushing-bite-force": [
    {
      q: "How strong is a jaguar's bite?",
      a: "Pound for pound it is the strongest bite of any big cat. A jaguar is far smaller than a lion or tiger yet bites with comparable force, thanks to a short, wide skull and heavy jaw muscles.",
    },
    {
      q: "Why does a jaguar bite the skull?",
      a: "Because it can. Most big cats suffocate prey with a throat bite; the jaguar's jaws are strong enough to punch through the skull or a turtle shell directly, which ends the fight faster.",
    },
    {
      q: "Do lions eat jaguars?",
      a: "They never meet. Lions live in Africa and a small part of India, jaguars in the Americas, so there is no wild encounter between them.",
    },
  ],

  "hurricane-storm-surge-destruction": [
    {
      q: "What is storm surge?",
      a: "Sea water pushed inland by a storm. It is not rain and it is not a tsunami: the hurricane's winds drag ocean water toward the coast and its low pressure lets the surface rise, so the sea level itself climbs above normal tide.",
    },
    {
      q: "Why is storm surge the deadliest part of a hurricane?",
      a: "Water weighs a lot and it arrives fast. Surge can raise the sea by several metres in hours, and moving water at that depth flattens buildings and sweeps away vehicles long before wind alone would.",
    },
  ],
};


export function faqsForSlug(slug: string): Faq[] {
  return ARTICLE_FAQS[slug] ?? [];
}
